// import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

const Banner = () => {
  const [typewriterText1, setTypewriterText1] = useState('');
  const [typewriterText2, setTypewriterText2] = useState('');
  const [showCursor1, setShowCursor1] = useState(true);
  const [showCursor2, setShowCursor2] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  const text1 = "Building Tanzania's Digital Future.";
  const text2 = "Your Excellence Deserves Recognition.";

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let intervalId: ReturnType<typeof setInterval>;

    const runTypewriterCycle = () => {
      let index1 = 0;
      let index2 = 0;
      let isErasing = false;

      // Type first text
      intervalId = setInterval(() => {
        if (!isErasing && index1 < text1.length) {
          setTypewriterText1(text1.slice(0, index1 + 1));
          index1++;
        } else if (!isErasing && index1 === text1.length) {
          clearInterval(intervalId);
          setShowCursor1(false);
          setShowCursor2(true);

          // Type second text
          timeoutId = setTimeout(() => {
            intervalId = setInterval(() => {
              if (!isErasing && index2 < text2.length) {
                setTypewriterText2(text2.slice(0, index2 + 1));
                index2++;
              } else if (!isErasing && index2 === text2.length) {
                clearInterval(intervalId);
                setShowCursor2(false);

                // Wait 5 seconds before erasing
                timeoutId = setTimeout(() => {
                  isErasing = true;
                  setShowCursor2(true);

                  // Erase second text
                  intervalId = setInterval(() => {
                    if (index2 > 0) {
                      index2--;
                      setTypewriterText2(text2.slice(0, index2));
                    } else {
                      clearInterval(intervalId);
                      setShowCursor2(false);
                      setShowCursor1(true);

                      // Erase first text
                      timeoutId = setTimeout(() => {
                        intervalId = setInterval(() => {
                          if (index1 > 0) {
                            index1--;
                            setTypewriterText1(text1.slice(0, index1));
                          } else {
                            clearInterval(intervalId);
                            // Restart the cycle
                            timeoutId = setTimeout(() => {
                              runTypewriterCycle();
                            }, 500);
                          }
                        }, 30);
                      }, 100);
                    }
                  }, 30);
                }, 5000);
              }
            }, 50);
          }, 300);
        }
      }, 50);
    };

    runTypewriterCycle();

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, []);

  return (
    <div id="app" className="relative w-full h-screen overflow-hidden">
      <div className="absolute inset-0 z-0">
        {/* Video for all devices - given priority */}
        <video 
          autoPlay 
          muted 
          loop 
          playsInline 
          preload="auto"
          className="w-full h-full object-cover"
          style={{ filter: 'brightness(0.7)' }}
          onLoadedData={() => setVideoLoaded(true)}
          onCanPlay={() => setVideoLoaded(true)}
          onError={() => setVideoLoaded(false)}
        >
          <source src="/assets/07.mp4" type="video/mp4" />
        </video>
        
        {/* Trophy image as fallback only if video fails to load */}
        <img 
          src="/trophy.jpg" 
          alt="Trophy" 
          className={`w-full h-full object-cover absolute inset-0 transition-opacity duration-500 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
          style={{ filter: 'brightness(0.7)' }}
        />
      </div>
      {/* Circular Graphic with Star */}
      <div className="absolute top-1/2 right-5 md:right-20 transform -translate-y-1/2 z-10 hidden sm:block">
        <div className="relative w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 flex items-center justify-center banner-circle">
          {/* Outer Circle */}
          <div className="absolute inset-0 border-3 md:border-4 rounded-full animate-spin-slow" style={{ borderColor: '#ffba17' }}></div>
          
          {/* Circular Text */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-full h-full" viewBox="0 0 200 200">
              <defs>
                <path id="circle" d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0"></path>
              </defs>
              <text fontSize="12" fill="white" className="font-medium">
                <textPath href="#circle" startOffset="0%">
                  Creativity • Innovation • Accountability •
                </textPath>
              </text>
            </svg>
          </div>
          
          {/* Center Star */}
          <div className="relative z-10">
            <i className="fa-solid fa-star text-2xl md:text-3xl lg:text-4xl" style={{ color: '#ffba17' }}></i>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-0 sm:px-12 md:px-16 lg:px-20 py-12 md:py-20">
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-full max-w-5xl px-0 sm:px-8 md:px-12">
            <div className="text-white text-left pl-8 md:pl-0">
              {/* Main Heading */}
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 md:mb-12 leading-tight"
                style={{ color: '#ffffff' }}
              >
                Celebrating Digital <br />
                Excellence
                <br />
                in Tanzania
              </h1>
              
              {/* Sub-heading */}
              <div className="mb-4 md:mb-8">
                <p 
                  className="text-xl md:text-2xl leading-relaxed font-medium"
                  style={{ color: '#ffffff' }}
                >
                  Recognizing Visionaries. Inspiring Innovation.
                </p>
              </div>
              
              {/* Additional Taglines */}
              <div className="space-y-2 md:space-y-4 mb-8 md:mb-12">
                <p 
                  className="text-lg md:text-xl leading-relaxed"
                  style={{ color: '#ffffff', minHeight: '1.75rem' }}
                >
                  {typewriterText1}
                  {showCursor1 && <span className="animate-pulse">|</span>}
                </p>
                <p 
                  className="text-lg md:text-xl leading-relaxed"
                  style={{ color: '#ffffff', minHeight: '1.75rem' }}
                >
                  {typewriterText2}
                  {showCursor2 && <span className="animate-pulse">|</span>}
                </p>
              </div>
              
              {/* CTA Button */}
              {/* <div className="mt-8 md:mt-12">
                <Link
                  to="/winners?id=4"
                  className="inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 md:px-10 md:py-5 bg-orange-500 text-white rounded-full font-semibold text-base sm:text-lg md:text-xl hover:bg-orange-600 transition-colors duration-300 group">
                  <span>Discover Our Digital Champions</span>
                  <i className="fa-solid fa-arrow-right ml-2 md:ml-3 group-hover:translate-x-1 transition-transform duration-300"></i>
                </Link>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Banner;
